import React, { useState } from "react";
import { makeRequestAuth } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { FiX, FiEdit, FiHash, FiClock } from "react-icons/fi";

export default function EditPaperModal({ data, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    paperName: data?.paperName || data?.name || "",
    totalQuestions: data?.totalQuestions || "",
    durationMinutes: data?.durationMinutes || "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !formData.paperName.trim() ||
      !formData.totalQuestions ||
      !formData.durationMinutes
    ) {
      return showErrorToast("All fields are required");
    }

    try {
      setLoading(true);
      await makeRequestAuth("post", "/admin/updatePaper", {
        paperId: data._id,
        paperName: formData.paperName,
        totalQuestions: Number(formData.totalQuestions),
        durationMinutes: Number(formData.durationMinutes),
      });

      showSuccessToast("Paper updated successfully");
      onSuccess();
    } catch (error) {
      showErrorToast(
        error?.response?.data?.message || "Failed to update paper",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm transition-all">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* HEADER */}
        <div className="flex justify-between items-center px-6 py-4 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            <div className="bg-blue-100 p-2 rounded-lg text-blue-600">
              <FiEdit size={20} />
            </div>
            <h3 className="text-lg font-bold text-gray-800">Edit Paper</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-200 text-gray-500 transition"
          >
            <FiX size={20} />
          </button>
        </div>

        {/* BODY */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Paper Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Paper Name
            </label>
            <input
              type="text"
              name="paperName"
              value={formData.paperName}
              onChange={handleChange}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Total Questions */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Total Questions
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                  <FiHash />
                </div>
                <input
                  type="number"
                  name="totalQuestions"
                  min="1"
                  value={formData.totalQuestions}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                />
              </div>
            </div>

            {/* Duration Minutes */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Duration (Min)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                  <FiClock />
                </div>
                <input
                  type="number"
                  name="durationMinutes"
                  min="1"
                  value={formData.durationMinutes}
                  onChange={handleChange}
                  placeholder="e.g. 60"
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                />
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="order-2 sm:order-1 flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="order-1 sm:order-2 flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-lg shadow-blue-500/30 font-medium disabled:opacity-50 transition-all"
            >
              {loading ? "Updating..." : "Update Paper"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
